import { useState, type CSSProperties } from "react";
import { isDexVenue } from "../lib/venues.ts";
import { DepthCard } from "../components/DepthCard.tsx";
import { DexDepthCard } from "../components/DexDepthCard.tsx";
import { DexTickLiquidityChart } from "../components/DexTickLiquidityChart.tsx";
import { DepthLadder } from "../components/DepthLadder.tsx";
import { DepthChart } from "../components/DepthChart.tsx";
import { useDepthBuckets } from "../hooks/useDepthBuckets.ts";
import { latestBucket } from "../lib/depth.ts";
import type { Binding } from "../bindings.ts";

// window maps to lookback_minutes. cadence is fixed at the standing 5m
// precompute (300s). 1m forensic buckets are a CLI/on-demand capability for
// incident investigation, not surfaced here.
// 1h is the shortest window: at 5m cadence that is 12 buckets — enough for a
// meaningful time series. 15m would yield only 1-3 points (no trend to read).
const WINDOWS = [
  { label: "1h", minutes: 60 },
  { label: "4h", minutes: 240 },
  { label: "1d", minutes: 1440 },
];
const BUCKET_SECONDS = 300;

const seg = (active: boolean): CSSProperties => ({
  padding: "3px 9px",
  fontSize: 12,
  lineHeight: 1.4,
  cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)",
  background: active ? "rgba(102,153,255,0.18)" : "transparent",
  color: active ? "#cfe0ff" : "#8a93a6",
  borderRadius: 5,
});

export function DepthView({ binding }: { binding: Binding }) {
  const [windowMinutes, setWindowMinutes] = useState(240); // default 4h

  // shared read for the latest-bucket quote asset label (card/ladder have their
  // own queries; this is just for the chart's y-axis unit). CEX only — DEX has
  // no precomputed buckets.
  const latest = useDepthBuckets({
    venue: binding.venue,
    pair: binding.pair,
    clientId: binding.clientId,
  });
  const quoteAsset = latestBucket(latest.data?.buckets)?.quote_asset ?? undefined;

  const dex = isDexVenue(binding.venue);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>{binding.label}</h3>
        <div className="panel-meta">
          <span style={{ fontSize: 11, color: "#8a93a6" }}>
            {dex
              ? "simulated pool-curve depth · bps bands · quote notional"
              : "band-bounded book depth · ±0.5 / 1 / 2% · quote notional"}
          </span>
        </div>
      </div>

      {dex ? (
        <>
          {/* DEX: live simulated pool-curve depth (card + ladder both read the
              live-profile endpoint; the bucket-based card/time-chart are not
              available because DEX is not in the depth precompute). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, margin: "8px 0 16px" }}>
            <DexDepthCard
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
            <DepthLadder
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
          </div>

          {/* Native CLMM tick-liquidity distribution (current snapshot). */}
          <div style={{ margin: "4px 0 12px" }}>
            <DexTickLiquidityChart
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
          </div>

          <div style={{ fontSize: 11, color: "#8a93a6", lineHeight: 1.5, marginTop: 4 }}>
            Liquidity here is the pool's concentrated-liquidity curve, simulated
            into cumulative quote-notional you could trade before moving the pool
            price by each bps band (method <code>clamm_slippage_simulated</code>).
            Depth-over-time is not yet available for DEX venues.
          </div>
        </>
      ) : (
        <>
          {/* at-a-glance: detail card + profile ladder */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, margin: "8px 0 16px" }}>
            <DepthCard
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
            <DepthLadder
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
          </div>

          {/* window control (cadence fixed at 5m) */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, margin: "4px 0 12px", alignItems: "center" }}>
            <div style={{ display: "inline-flex", gap: 4 }} role="group" aria-label="Window">
              {WINDOWS.map((w) => (
                <button
                  key={w.minutes}
                  style={seg(w.minutes === windowMinutes)}
                  onClick={() => setWindowMinutes(w.minutes)}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: "#8a93a6" }}>5-minute buckets</span>
          </div>

          {/* depth over time */}
          <div style={{ fontWeight: 500, color: "#cfe0ff", margin: "8px 0 4px" }}>
            Depth over time
          </div>
          <DepthChart
            venue={binding.venue}
            pair={binding.pair}
            clientId={binding.clientId}
            lookbackMinutes={windowMinutes}
            bucketSeconds={BUCKET_SECONDS}
            quoteAsset={quoteAsset}
          />
        </>
      )}
    </div>
  );
}
